// Shared data hooks: the cast directory, a ticking clock, the fleet as map
// actors. Components never read the clock during render; they get `now` from
// useNow (state updated on an interval).
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { PERSONAS } from '../../convex/shared/scenario';
import type { PersonaCard, VehicleView } from '../../convex/shared/views';
import type { Actor } from '../map/SimScene';
import { hexToRgb, humanize, shortName } from './format';
import { useSimQuery } from './session';

export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export type Cast = {
  cards: PersonaCard[];
  byId: Map<Id<'personas'>, PersonaCard>;
  byKey: Map<string, PersonaCard>;
  nameOf: (id: Id<'personas'> | undefined | null) => string;
};

export function useCast(): Cast | undefined {
  const cards = useSimQuery(api.agents.personas.listPersonaCards, {});
  return useMemo(() => {
    if (!cards) return undefined;
    const byId = new Map(cards.map((c) => [c._id, c]));
    const byKey = new Map(cards.map((c) => [c.key, c]));
    return {
      cards,
      byId,
      byKey,
      nameOf: (id) => (id ? shortName(byId.get(id)?.name ?? 'Someone') : 'Nobody'),
    };
  }, [cards]);
}

/** Email address → persona key (aa-app mails land at <first>@<crew domain>). */
export function personaKeyForAddress(address: string): string | undefined {
  const local = address.toLowerCase().split('@')[0]?.replace(/^.*</, '');
  return PERSONAS.find((p) => p.aaApp.emailLocalPart === local)?.key;
}

export function driverLabel(v: VehicleView, cast: Cast | undefined): string | undefined {
  if (v.driver.kind === 'persona') return cast?.nameOf(v.driver.personaId);
  if (v.driver.kind === 'human') return 'The owner';
  return undefined;
}

export function vehicleCaption(v: VehicleView, cast: Cast | undefined): string {
  const who = driverLabel(v, cast);
  if (v.state === 'driving' && who) return `${who} driving`;
  if (v.state === 'towed') return 'Towed';
  return who ? `${humanize(v.state)} · ${who}` : humanize(v.state);
}

export function toActor(v: VehicleView, cast: Cast | undefined): Actor {
  return {
    id: v._id,
    kind: v.kind,
    name: v.name.replace(/\s*\(.*\)\s*$/, ''),
    color: hexToRgb(v.color),
    hitchedTo: v.hitchedToVehicleId,
    caption: vehicleCaption(v, cast),
    fallback: {
      lat: v.lat,
      lng: v.lng,
      headingDeg: v.headingDeg,
      speedKmh: v.speedKmh,
      accel: 0,
      flags: v.engineOn ? 1 : 0,
      limitKmh: 0,
    },
  };
}
